// Shared display config for TaskStep statuses.
// Consumers: WorkflowTable's StatusCellRenderer (pill variant) and the
// drawer's TaskList (flat variant). Keeping the icon/label/color mapping
// in one place ensures both surfaces stay in lockstep when the BE adds a
// status or we relabel one.

import type { IconType } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { TASK_STEP_STATUS_LABELS, GPX_STATUS_LABELS } from "./constants";
import type { TaskStepStatus, GpxStatus } from "./types";

export interface TaskStatusDisplay {
  label: string;
  icon: IconType;
  iconColor: Colors;
  textColor: Colors;
}

// NOT_STARTED is the BE alias for BLOCKED — both render identically here.
export function getTaskStatusDisplay(status: string): TaskStatusDisplay {
  const label = TASK_STEP_STATUS_LABELS[status as TaskStepStatus] ?? status;
  switch (status) {
    case "COMPLETED":
      return {
        label,
        icon: "check",
        iconColor: Colors.Success600,
        textColor: Colors.Success600,
      };
    case "IN_PROGRESS":
      return {
        label,
        icon: "sync-alt",
        iconColor: Colors.Primary500,
        textColor: Colors.Primary500,
      };
    case "PENDING_REVIEW":
      return {
        label,
        icon: "hourglass-start",
        iconColor: Colors.Primary500,
        textColor: Colors.Primary500,
      };
    case "EXTERNAL_APPROVAL":
      return {
        label,
        icon: "clock",
        iconColor: Colors.Warning600,
        textColor: Colors.Warning600,
      };
    case "NOT_STARTED":
    case "BLOCKED":
      return {
        label,
        icon: "lock",
        iconColor: Colors.Neutral400,
        textColor: Colors.Neutral500,
      };
    case "SKIPPED":
      return {
        label,
        icon: "minus-circle",
        iconColor: Colors.Neutral400,
        textColor: Colors.Neutral500,
      };
    case "RETURNED":
      return {
        label,
        icon: "arrow-left",
        iconColor: Colors.Danger600,
        textColor: Colors.Danger600,
      };
    default:
      return {
        label,
        icon: "clock",
        iconColor: Colors.Neutral500,
        textColor: Colors.Neutral500,
      };
  }
}

// Statuses where the row is non-interactive (no click, lock icon).
// BLOCKED + NOT_STARTED are treated as one bucket per BE contract.
export function isTaskStatusBlocked(status: string): boolean {
  return status === "BLOCKED" || status === "NOT_STARTED";
}

// GP-facing (GPX) status display config — used in the GPX portal where GPs
// see a simplified view of task progress.
export function getGpxStatusDisplay(gpxStatus: string): TaskStatusDisplay {
  const label = GPX_STATUS_LABELS[gpxStatus as GpxStatus] ?? gpxStatus;
  switch (gpxStatus) {
    case "COMPLETE":
      return {
        label,
        icon: "check",
        iconColor: Colors.Success600,
        textColor: Colors.Success600,
      };
    case "IN_PROGRESS":
      return {
        label,
        icon: "sync-alt",
        iconColor: Colors.Primary500,
        textColor: Colors.Primary500,
      };
    case "READY_FOR_REVIEW":
      return {
        label,
        icon: "hourglass-start",
        iconColor: Colors.Warning600,
        textColor: Colors.Warning600,
      };
    case "IN_PROGRESS_RETURNED":
      return {
        label,
        icon: "arrow-left",
        iconColor: Colors.Danger600,
        textColor: Colors.Danger600,
      };
    case "NOT_STARTED":
      return {
        label,
        icon: "lock",
        iconColor: Colors.Neutral400,
        textColor: Colors.Neutral500,
      };
    default:
      return {
        label,
        icon: "clock",
        iconColor: Colors.Neutral500,
        textColor: Colors.Neutral500,
      };
  }
}
